/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  AppFormUpload,
  Checkbox,
  ControlledInput,
  ControlledSelect,
  FormColorPicker,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm, SubmitHandler } from "react-hook-form";
import { useEffect, useState } from "react";
import { useAlert } from "../../store/useAlert";
import "./richtext.css";
import { useOnboardings } from "../../store/useOnboardings";
import { useDirectUploadImage } from "../../store/useDirectUploadImage";

type Inputs = {
  page: string;
  title: string;
  resizeType: "contain" | "cover";
  enCustomButtonText?: string;
  nlCustomButtonText?: string;
};

function AddOnboardingScreen() {
  /** Hooks */
  const { setAlert } = useAlert();
  const { addOnboarding, addedOnboarding, clearAddedOnboarding } =
    useOnboardings();
  const {
    uploadImage: uploadEnImage,
    clearUploadedImage: clearEnUploadedImage,
    uploadedImage: uploadedEnImage,
  } = useDirectUploadImage();
  const {
    uploadImage: uploadNlImage,
    clearUploadedImage: clearNlUploadedImage,
    uploadedImage: uploadedNlImage,
  } = useDirectUploadImage();
  const navigate = useNavigate();
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [enImage, setEnImage] = useState<File | null>(null);
  const [nlImage, setNlImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [showFooterProgress, setShowFooterProgress] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false);
  const [enTopBackgroundColor, setEnTopBackgroundColor] = useState<string>("");
  const [nlTopBackgroundColor, setNlTopBackgroundColor] = useState<string>("");
  const [enBottomBackgroundColor, setEnBottomBackgroundColor] =
    useState<string>("");
  const [nlBottomBackgroundColor, setNlBottomBackgroundColor] =
    useState<string>("");
  const [showCustomButton, setShowCustomButton] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!enImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image in English language.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!nlImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image in Dutch language.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedEnImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image in English language.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedNlImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image in Dutch language.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    setSubmitted(false);
    const body = {
      ...data,
      image_en: uploadedEnImage,
      image_nl: uploadedNlImage,
      showHeader,
      showNextButton,
      showFooterProgress,
      enTopBackgroundColor,
      nlTopBackgroundColor,
      enBottomBackgroundColor,
      nlBottomBackgroundColor,
      showCustomButton,
    };
    addOnboarding(body);
  };

  const goBack = () => navigate(-1);

  useEffect(() => {
    return () => {
      clearEnUploadedImage();
      clearNlUploadedImage();
      clearAddedOnboarding();
    };
  }, []);

  useEffect(() => {
    if (addedOnboarding) {
      navigate(-1);
    }
  }, [addedOnboarding]);

  const onEnImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setEnImage(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (enImage) {
      onEnUploadImage();
    }
  }, [enImage]);

  const onEnUploadImage = () => {
    if (!enImage) return;
    uploadEnImage(enImage);
  };

  const enImageError = submitted && !enImage;

  const onNlImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setNlImage(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (nlImage) {
      onNlUploadImage();
    }
  }, [nlImage]);

  const onNlUploadImage = () => {
    if (!nlImage) return;
    uploadNlImage(nlImage);
  };

  const nlImageError = submitted && !nlImage;

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new Onboarding screen
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-0"
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New Onboarding information
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.page)}
              label="Page number"
              placeholder="Page number"
              name="page"
              rules={{ required: true, pattern: /^[0-9]*$/ }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.title)}
              label="Title"
              placeholder="Title"
              name="title"
              rules={{ required: true }}
            />
            <Checkbox
              value={showHeader}
              onChange={(e) => setShowHeader(e.target.checked)}
              label="Show header (Skip button)"
            />
            <Checkbox
              label="Show footer progress"
              value={showFooterProgress}
              onChange={(e) => setShowFooterProgress(e.target.checked)}
            />
            <Checkbox
              label="Show next button"
              value={showNextButton}
              onChange={(e) => {
                setShowCustomButton(false);
                setShowNextButton(e.target.checked);
              }}
            />
            <Checkbox
              label="Show custom button text"
              value={showCustomButton}
              onChange={(e) => {
                setShowNextButton(false);
                setShowCustomButton(e.target.checked);
              }}
            />
            {showCustomButton ? (
              <ControlledInput
                control={control}
                error={Boolean(errors?.enCustomButtonText)}
                label="Custom button label (EN)"
                placeholder="Custom button label in English..."
                name="enCustomButtonText"
                rules={{ required: true }}
              />
            ) : null}
            {showCustomButton ? (
              <ControlledInput
                control={control}
                error={Boolean(errors?.nlCustomButtonText)}
                label="Custom button label (NL)"
                placeholder="Custom button label in Dutch..."
                name="nlCustomButtonText"
                rules={{ required: true }}
              />
            ) : null}
            <ControlledSelect
              name="resizeType"
              control={control}
              rules={{ required: false }}
              error={Boolean(errors?.resizeType)}
              label="Resize image type"
              options={[
                { _id: "contain", title: "Contain" },
                { _id: "cover", title: "Cover" },
              ]}
              placeholder="Pick image resize type here"
              inputClassName="w-full"
              className="w-full"
            />
            <FormColorPicker
              label="Add Top color (EN)"
              value={enTopBackgroundColor}
              onChange={setEnTopBackgroundColor}
            />
            <FormColorPicker
              label="Add Bottom color (EN)"
              value={enBottomBackgroundColor}
              onChange={setEnBottomBackgroundColor}
            />
            <AppFormUpload
              label="Onboarding screen image (EN)"
              onChange={onEnImageChange}
              value={enImage}
              error={enImageError}
              accept=".png, .jpg, .jpeg, .webp"
              showHeader={showHeader}
              showFooterProgress={showFooterProgress}
              showNextButton={showNextButton}
              resizeType={watch("resizeType")}
            />
            <FormColorPicker
              label="Add Top color (NL)"
              value={nlTopBackgroundColor}
              onChange={setNlTopBackgroundColor}
            />
            <FormColorPicker
              label="Add Bottom color (NL)"
              value={nlBottomBackgroundColor}
              onChange={setNlBottomBackgroundColor}
            />
            <AppFormUpload
              label="Onboarding screen image (NL)"
              onChange={onNlImageChange}
              value={nlImage}
              error={nlImageError}
              accept=".png, .jpg, .jpeg, .webp"
              showHeader={showHeader}
              showFooterProgress={showFooterProgress}
              showNextButton={showNextButton}
              resizeType={watch("resizeType")}
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddOnboardingScreen);
